import { useState, useEffect, useRef } from "react";

/**
 * Custom hook to manage sticky button behavior on mobile devices
 * Makes buttons float at the bottom when not visible, returns to normal position when scrolled into view
 */
const useStickyButton = () => {
  const [isSticky, setIsSticky] = useState(false);
  const [buttonHeight, setButtonHeight] = useState(0);
  const buttonContainerRef = useRef(null);
  const sentinelRef = useRef(null); // Sentinel element to observe instead of the button
  const observerRef = useRef(null);

  useEffect(() => {
    let sentinel = sentinelRef.current;

    let rafId;
    let scrollRafId;
    let resizeObserver;
    let mediaQuery;
    let mqListener;

    // Check if device is mobile/tablet
    const isMobileNow = () => {
      try {
        mediaQuery = window.matchMedia("(max-width: 768px)");
        return mediaQuery.matches;
      } catch {
        return window.innerWidth <= 768;
      }
    };

    const checkAndSetupObserver = () => {
      const isMobile = isMobileNow();
      
      if (!isMobile) {
        setIsSticky(false);
        if (observerRef.current) {
          observerRef.current.disconnect();
          observerRef.current = null;
        }
        return;
      }

      // Disconnect existing observer if any
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      // Function to check if button has enough space or is being cut off
      const checkButtonVisibility = () => {
        const button = buttonContainerRef.current;
        const sentinelElement = sentinelRef.current;
        if (!button || !sentinelElement) return;

        const sentinelRect = sentinelElement.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        
        // Get button height (use actual height even if it's fixed positioned)
        const currentButtonHeight = button.offsetHeight || button.getBoundingClientRect().height;
        
        // Update button height state if changed (for spacer)
        if (currentButtonHeight !== buttonHeight) {
          setButtonHeight(currentButtonHeight);
        }
        
        // Calculate if there's enough space from sentinel to bottom of viewport
        // The sentinel marks where the button would naturally sit
        const spaceAvailable = viewportHeight - sentinelRect.top;
        const spaceNeeded = currentButtonHeight + 20; // 20px buffer for safety (padding + margin)
        
        // Make sticky if not enough space, otherwise keep it in normal position
        setIsSticky(spaceAvailable < spaceNeeded);
      };

      // Create observer to watch for scroll changes
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            // Check button visibility whenever sentinel intersection changes
            checkButtonVisibility();
          });
        },
        {
          root: null,
          threshold: [0, 0.1, 0.5, 1], // Multiple thresholds for more accurate detection
          rootMargin: "0px",
        }
      );

      // Use requestAnimationFrame to wait for layout to complete
      rafId = requestAnimationFrame(() => {
        sentinel = sentinelRef.current;
        if (observerRef.current && sentinel) {
          // Check initial state
          checkButtonVisibility();
          // Then start observing for changes
          observerRef.current.observe(sentinel);
        }
      });
    };

    // If sentinel not ready yet (e.g., loading overlay), wait until it is
    const waitForSentinel = () => {
      sentinel = sentinelRef.current;
      if (!sentinel) {
        rafId = requestAnimationFrame(waitForSentinel);
        return;
      }
      checkAndSetupObserver();
    };

    // Initial setup - wait for layout to complete or sentinel to mount
    waitForSentinel();

    // Observe layout/size changes to recompute visibility
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        if (!isMobileNow()) return;
        
        const button = buttonContainerRef.current;
        const sentinelElement = sentinelRef.current;
        if (!button || !sentinelElement) return;
        
        const sentinelRect = sentinelElement.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const buttonHeight = button.offsetHeight || button.getBoundingClientRect().height;
        
        const spaceAvailable = viewportHeight - sentinelRect.top;
        const spaceNeeded = buttonHeight + 20;
        
        setIsSticky(spaceAvailable < spaceNeeded);
      });
      try { resizeObserver.observe(document.documentElement); } catch {}
      if (buttonContainerRef.current) {
        try { resizeObserver.observe(buttonContainerRef.current); } catch {}
      }
      if (sentinelRef.current) {
        try { resizeObserver.observe(sentinelRef.current); } catch {}
      }
    }

    // Handle window resize
    const handleResize = () => {
      checkAndSetupObserver();
    };

    // Handle scroll to check button visibility in real-time (throttled with RAF)
    let isScrolling = false;
    const handleScroll = () => {
      if (!isMobileNow() || isScrolling) return;
      
      isScrolling = true;
      scrollRafId = requestAnimationFrame(() => {
        const button = buttonContainerRef.current;
        const sentinelElement = sentinelRef.current;
        
        if (button && sentinelElement) {
          const sentinelRect = sentinelElement.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          const buttonHeight = button.offsetHeight || button.getBoundingClientRect().height;
          
          const spaceAvailable = viewportHeight - sentinelRect.top;
          const spaceNeeded = buttonHeight + 20;
          
          setIsSticky(spaceAvailable < spaceNeeded);
        }
        
        isScrolling = false;
      });
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);
    window.addEventListener("load", handleResize);
    window.addEventListener("pageshow", handleResize);
    window.addEventListener("scroll", handleScroll, { passive: true });

    // Listen to media query changes (e.g., when switching device emulation)
    try {
      mediaQuery = window.matchMedia("(max-width: 768px)");
      mqListener = () => handleResize();
      if (mediaQuery.addEventListener) mediaQuery.addEventListener("change", mqListener);
      else if (mediaQuery.addListener) mediaQuery.addListener(mqListener);
    } catch {}

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      if (scrollRafId) {
        cancelAnimationFrame(scrollRafId);
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (resizeObserver) {
        try {
          resizeObserver.disconnect();
        } catch {}
      }
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
      window.removeEventListener("load", handleResize);
      window.removeEventListener("pageshow", handleResize);
      window.removeEventListener("scroll", handleScroll);
      try {
        if (mediaQuery && mqListener) {
          if (mediaQuery.removeEventListener) mediaQuery.removeEventListener("change", mqListener);
          else if (mediaQuery.removeListener) mediaQuery.removeListener(mqListener);
        }
      } catch {}
    };
  }, []);

  return {
    buttonContainerRef,
    sentinelRef,
    isSticky,
    buttonHeight, // Height of button for spacer
  };
};

export default useStickyButton;
